/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:KlangScript.Library(KlangScriptLibraries.STDLIB)

package io.peekandpoke.klang.script.stdlib

import io.peekandpoke.klang.audio_bridge.AdsrBuilder
import io.peekandpoke.klang.audio_bridge.AdsrCurve
import io.peekandpoke.klang.audio_bridge.AdsrCurves
import io.peekandpoke.klang.audio_bridge.BandFilterBuilder
import io.peekandpoke.klang.audio_bridge.EqBuilder
import io.peekandpoke.klang.audio_bridge.FilterBuilder
import io.peekandpoke.klang.audio_bridge.FmBuilder
import io.peekandpoke.klang.audio_bridge.IgnitorDsl
import io.peekandpoke.klang.audio_bridge.KatalystEqBuilder
import io.peekandpoke.klang.audio_bridge.LfoShapes
import io.peekandpoke.klang.audio_bridge.ModAdsrBuilder
import io.peekandpoke.klang.audio_bridge.PhaserBuilder
import io.peekandpoke.klang.audio_bridge.PitchEnvelopeBuilder
import io.peekandpoke.klang.audio_bridge.ShimmerBuilder
import io.peekandpoke.klang.audio_bridge.TremoloBuilder
import io.peekandpoke.klang.audio_bridge.VibratoBuilder
import io.peekandpoke.klang.audio_bridge.constants.MOD_ENV_CURVE
import io.peekandpoke.klang.script.annotations.KlangScript
import io.peekandpoke.klang.script.annotations.KlangScriptLibraries

/*
 * Builders for the wrappers that carry secondary knobs: the four filters, the equalizer's
 * sections, the pitch envelope, fm's index envelope, the envelopes' own `adsr` builders, the dry
 * floor of phaser and shimmer, and the tremolo's and the vibrato's LFO knobs. Same
 * shape as the oscillator builders (`IgnitorBuilders.kt`): immutable values, one knob = one
 * `@KlangScript.Function` extension. The door keeps the stage's musical inputs, the builder the
 * rest (`/dsl-design` section 2). Every builder here lives in `audio_bridge` with typed member knobs (`VibratoBuilder`
 * since S1; the filters' `FilterBuilder`, `BandFilterBuilder` and `ModAdsrBuilder`, the eq's `EqBuilder` and
 * `KatalystEqBuilder` since S3a; `AdsrBuilder`, `PitchEnvelopeBuilder` and `FmBuilder` since S3b; `PhaserBuilder`,
 * `ShimmerBuilder` and `TremoloBuilder` since S3c); their script knobs here are `internal`, renamed variants that
 * convert and call the members (`/dsl-design` section 3, the typed-door pattern).
 *
 *     Ignitor.saw().eq(e => e.band(300, 1.0, -4).tap(850, 0.707, 1.7)).lowpass(5000)
 *     Ignitor.saw().lowpass(800, 1.2, x => x.passes(2).env(24).adsr(0.005, 0.3, 0.2, 0.2, e => e.curves("lin", "exp", "exp")))
 *     Ignitor.saw().adsr(0.01, 0.3, 0.5, 0.2, e => e.curves("square", "exp", "exp").declick(0.0005))
 *     Ignitor.saw().phaser(0.3, 0.5, x => x.floor(0.2))
 */

// ── Envelopes ────────────────────────────────────────────────────────────────

/*
 * `AdsrBuilder` (the amplitude envelope's, knobs `curves` and `declick` since phase 3 step 3c, 2026-09-25, which
 * replaced the reach-back chain methods `adsrCurves` and `declickSeconds`) and `ModAdsrBuilder` live in `audio_bridge`
 * (`EffectBuilders.kt` there) with their typed member knobs; the script knobs below convert and call them.
 */

/**
 * One `curves` argument as the curve knob it sets, for an envelope whose default is [fallback]:
 * a NAME through [AdsrCurves] (an unknown one is [fallback]), a number or a slot as it is, and an
 * OMITTED one as [fallback] too. So every `curves` call sets all three stages, and a later call
 * replaces an earlier one completely: the rule `adsrCurves` always had.
 */
internal fun curveKnob(value: IgnitorDslLike?, fallback: AdsrCurve): IgnitorDsl =
    catalogueIndex(value, IgnitorDsl.Constant(AdsrCurves.indexOf(fallback)), indexOf = { AdsrCurves.indexOf(it, fallback) })

/**
 * Shapes the amplitude envelope's stages: `"exp"` (the default, every amplitude envelope's),
 * `"linear"` (`lin`), `"square"` (`sq`/`quad`), `"cube"` (`cb`), `"scurve"` (`s`/`smooth`/`sigmoid`)
 * or `"invsquare"` (`inv`/`concave`). An omitted argument and an unknown name both mean `"exp"`, so
 * every call sets all three stages. Each argument may also be the curve's index in that list, or a
 * slot carrying it; it is chosen once per note. Every exponential stage bends at the engine's one
 * curvature (`ADSR_EXP_K`).
 */
@KlangScript.Function(name = "curves")
internal fun AdsrBuilder.scriptCurves(
    attack: IgnitorDslLike? = null,
    decay: IgnitorDslLike? = null,
    release: IgnitorDslLike? = null,
): AdsrBuilder = this.curves(
    attack = curveKnob(attack, AdsrCurve.Default),
    decay = curveKnob(decay, AdsrCurve.Default),
    release = curveKnob(release, AdsrCurve.Default),
)

/**
 * De-clicks the envelope's gain by [seconds]: a one-pole low-pass that rounds the corners at the
 * segment joins (the attack peak, the gate's end, the cutoff), removing the low-note "plop". `0` is
 * off, which is this envelope's default; a gentle value is about 0.0005 to 0.001.
 */
@KlangScript.Function(name = "declick")
internal fun AdsrBuilder.scriptDeclick(seconds: IgnitorDslLike): AdsrBuilder = this.declick(seconds = seconds.toIgnitorDsl())

/**
 * Shapes a modulation envelope's stages (a filter's cutoff envelope, the pitch envelope), with the
 * chain's six curves and their short names. Unshaped stages are `"exp"`, the house curve (decision
 * D3; they were linear before); an omitted argument and an unknown name both mean it, so every
 * call sets all three stages. A number or a slot is the curve's index, chosen once per note.
 */
@KlangScript.Function(name = "curves")
internal fun ModAdsrBuilder.scriptCurves(
    attack: IgnitorDslLike? = null,
    decay: IgnitorDslLike? = null,
    release: IgnitorDslLike? = null,
): ModAdsrBuilder = this.curves(
    attack = curveKnob(attack, MOD_ENV_CURVE),
    decay = curveKnob(decay, MOD_ENV_CURVE),
    release = curveKnob(release, MOD_ENV_CURVE),
)

// ── Filters ──────────────────────────────────────────────────────────────────

/*
 * `FilterBuilder` and `BandFilterBuilder` (with `FilterKnobs`, what the lambda collected for the compound fill) live in
 * `audio_bridge` (`EffectBuilders.kt` there, since the typed-doors rollout S3a) with their typed member knobs; the
 * script knobs below are `internal` extensions with a Kotlin name of their own (`scriptEnv`, the script word in
 * `name`) that convert and call those members (`/dsl-design` section 3, the typed-door pattern).
 */

/**
 * Cascade count: `2` = 24 dB/oct, `3` = 36. Rounded, coerced to 1..16. A number or a slot
 * (`Ignitor.slot.lpf.passes`), read once at voice build; a moving signal reads as one pass. The per-stage q is
 * STAGGERED (Butterworth ladder scaled by `q/0.707`), so at the default q the cascade stays -3 dB at
 * the cutoff; a resonant q COMPOUNDS across stages (`q = 1.0, passes = 2` sits +3 dB there).
 */
@KlangScript.Function(name = "passes")
internal fun FilterBuilder.scriptPasses(n: IgnitorDslLike): FilterBuilder = this.passes(n = n.toIgnitorDsl())

/**
 * Analog character, 0..10. `0` (the default) is the clean linear filter; higher values engage
 * OB-X-style state-dependent damping that compresses the resonance peak, 1 to 3 is Diva-default
 * warmth. It also scales [humanize]. Every cascade stage gets the full drive.
 */
@KlangScript.Function(name = "analog")
internal fun FilterBuilder.scriptAnalog(character: IgnitorDslLike): FilterBuilder = this.analog(character = character.toIgnitorDsl())

/**
 * Per-voice analog character, scaled by `analog`: a cutoff tolerance drawn once per note plus a
 * slow drift lane. Off by default; at `analog = 0` it changes nothing. Accepts `true`/`false` or a
 * number (`1` on, `0` off); anything else reads as ON (coerced, never thrown).
 */
@KlangScript.Function(name = "humanize")
internal fun FilterBuilder.scriptHumanize(on: Any = true): FilterBuilder = this.humanize(on = coerceFlag(on))

/**
 * Cutoff-envelope DEPTH in semitones: `cutoff = freq * 2^(env/12 * envelope)`. `env` and `adsr` are
 * a compound pair: naming either switches the envelope on and the other fills from
 * `audio_bridge/constants/FilterEnvelopeDefaults.kt` (the fill runs after the lambda, once). A
 * non-leaf EXPRESSION here is unreadable at build and switches the envelope OFF; write a number or
 * a slot (`Ignitor.slot.lpf.env`).
 */
@KlangScript.Function(name = "env")
internal fun FilterBuilder.scriptEnv(semitones: IgnitorDslLike): FilterBuilder = this.env(semitones = semitones.toIgnitorDsl())

/**
 * The cutoff envelope, ONE call, the chain `adsr`'s shape: attack, decay, sustain (a share of
 * `env`, 0 to 1) and release in seconds, and a lambda on a [ModAdsrBuilder] that shapes the stages
 * with `curves` (unshaped stages are `"exp"`, decision D3):
 * `.lowpass(800, 1.2, x => x.env(24).adsr(0.01, 0.3, 0.2, 0.5, e => e.curves("lin", "exp", "exp")))`.
 * Switches the envelope on; without `env` the depth fills from the constants. A later `adsr` call
 * replaces an earlier one completely, curves included. The release does NOT extend the voice's life.
 *
 * @param configure receives the [ModAdsrBuilder] (knob: `curves`) and returns it.
 */
@KlangScript.Function(name = "adsr")
internal fun FilterBuilder.scriptAdsr(
    attack: IgnitorDslLike,
    decay: IgnitorDslLike,
    sustain: IgnitorDslLike,
    release: IgnitorDslLike,
    configure: ((ModAdsrBuilder) -> ModAdsrBuilder)? = null,
): FilterBuilder = this.adsr(
    attack = attack.toIgnitorDsl(),
    decay = decay.toIgnitorDsl(),
    sustain = sustain.toIgnitorDsl(),
    release = release.toIgnitorDsl(),
) { it.configuredBy("adsr", configure) }

/**
 * The analog SATURATION is not implemented for this tap and the value does not reach it. The value
 * is NOT inert, though: it scales [humanize]'s per-voice tolerance and drift lane (as it did on the
 * retired voice strip's band filter), so `analog = 0` on a humanized band filter switches the lane off and stops
 * its four build-time rng draws, which shifts every later noise source on that voice.
 */
@KlangScript.Function(name = "analog")
internal fun BandFilterBuilder.scriptAnalog(character: IgnitorDslLike): BandFilterBuilder = this.analog(character = character.toIgnitorDsl())

/** Per-voice cutoff tolerance and drift lane, scaled by `analog`; see `FilterBuilder.humanize`. */
@KlangScript.Function(name = "humanize")
internal fun BandFilterBuilder.scriptHumanize(on: Any = true): BandFilterBuilder = this.humanize(on = coerceFlag(on))

/** Cutoff-envelope depth in semitones, compound with `adsr`; see `FilterBuilder.env`. */
@KlangScript.Function(name = "env")
internal fun BandFilterBuilder.scriptEnv(semitones: IgnitorDslLike): BandFilterBuilder = this.env(semitones = semitones.toIgnitorDsl())

/**
 * The cutoff envelope, one call with its `curves` lambda; see `FilterBuilder.adsr`.
 *
 * @param configure receives the [ModAdsrBuilder] (knob: `curves`) and returns it.
 */
@KlangScript.Function(name = "adsr")
internal fun BandFilterBuilder.scriptAdsr(
    attack: IgnitorDslLike,
    decay: IgnitorDslLike,
    sustain: IgnitorDslLike,
    release: IgnitorDslLike,
    configure: ((ModAdsrBuilder) -> ModAdsrBuilder)? = null,
): BandFilterBuilder = this.adsr(
    attack = attack.toIgnitorDsl(),
    decay = decay.toIgnitorDsl(),
    sustain = sustain.toIgnitorDsl(),
    release = release.toIgnitorDsl(),
) { it.configuredBy("adsr", configure) }

// ── Pitch envelope ───────────────────────────────────────────────────────────

/*
 * `PitchEnvelopeBuilder` lives in `audio_bridge` (`EffectBuilders.kt` there, since the typed-doors rollout S3b) with its
 * typed member `adsr`, which holds the envelope's default fill once for both doors; the script knob below converts and
 * calls it.
 */

/**
 * The pitch envelope, ONE call, the chain `adsr`'s shape: the pitch rises to `semitones` over the
 * attack, falls to the sustain (a share of `semitones`, default 0 = the note) over the decay, holds,
 * and from the gate's end returns to the note over the release. The lambda shapes the stages with
 * `curves`, the filters' rules (unshaped stages are `"exp"`, decision D3):
 * `.pitchEnvelope(24, x => x.adsr(0.001, 0.04, 0, 0, e => e.curves("lin", "exp", "exp")))`.
 * Defaults without this call: `adsr(0.01, 0.1, 0, 0)`, exponential. A later `adsr` call replaces an
 * earlier one completely, curves included. The release does NOT extend the voice's life.
 *
 * @param configure receives the [ModAdsrBuilder] (knob: `curves`) and returns it.
 */
@KlangScript.Function(name = "adsr")
internal fun PitchEnvelopeBuilder.scriptAdsr(
    attack: IgnitorDslLike,
    decay: IgnitorDslLike,
    sustain: IgnitorDslLike,
    release: IgnitorDslLike,
    configure: ((ModAdsrBuilder) -> ModAdsrBuilder)? = null,
): PitchEnvelopeBuilder = this.adsr(
    attack = attack.toIgnitorDsl(),
    decay = decay.toIgnitorDsl(),
    sustain = sustain.toIgnitorDsl(),
    release = release.toIgnitorDsl(),
) { it.configuredBy("adsr", configure) }

// ── FM ───────────────────────────────────────────────────────────────────────

/* `FmBuilder` lives in `audio_bridge` (`EffectBuilders.kt` there, since S3b) with its typed member `adsr`. */

/**
 * The modulation INDEX envelope: the depth rises over the attack, falls to the sustain (a share of
 * `depth`) over the decay, holds, and releases from the gate's end. Without this call the depth is
 * constant, which is `adsr(0, 0, 1, 0)`. The built-in `sgbell` is `adsr(0.001, 0.5, 0, 0.05)`. Its
 * stages are exponential (decision D3), and it takes no lambda yet: the FM index envelope has no curve
 * support (a filed follow-up adds `curves` when it has), and a knob that does nothing is not offered.
 */
@KlangScript.Function(name = "adsr")
internal fun FmBuilder.scriptAdsr(
    attack: IgnitorDslLike,
    decay: IgnitorDslLike,
    sustain: IgnitorDslLike,
    release: IgnitorDslLike,
): FmBuilder = this.adsr(
    attack = attack.toIgnitorDsl(),
    decay = decay.toIgnitorDsl(),
    sustain = sustain.toIgnitorDsl(),
    release = release.toIgnitorDsl(),
)

// ── Eq ───────────────────────────────────────────────────────────────────────

/*
 * The eq's two builders live in `audio_bridge` (`EffectBuilders.kt` there, since the typed-doors rollout S3a): the
 * voice's `EqBuilder` (knobs `IgnitorDsl`) and the chain's `KatalystEqBuilder` (knobs `KatalystKnobDsl`), the same words
 * and sections, one helper (`eqSectionKnobOrNull`) for the Katalyst section conversion and its refusal of an
 * expression. The script knobs of both are below: the voice's convert like a tree leaf ([toIgnitorDsl]), the chain's
 * like every chain knob ([toKatalystKnob]) and pre-check the refusal with a script error ([toEqSectionKatalystKnob]).
 */

/**
 * Adds a peaking band: [db] decibels of gain at [freq], [q] the width. Bands apply one after
 * another, so two overlapping bands compound.
 *
 * `db = 0` is exactly transparent, negative [db] cuts, and a cut mirrors the same boost exactly,
 * so a `+6` and a `-6` band at the same [freq] and [q] cancel out. The width does NOT move as
 * [db] changes (that is the point of this parameterization): at the default [q] it holds at
 * 1.90 octaves all the way from -6 to -34 dB. Past that the internal width hits its floor and
 * deeper cuts DO start narrowing (1.39 octaves at -40 dB, about 0.45 at -60).
 *
 * The second positional argument is [q], NOT gain: `band(1200, 6)` sets a width of 6 and leaves
 * gain at 0, which is silent. Write `band(freq = 1200, db = 6)` when you mean gain: KlangScript
 * forbids mixing positional and named args, so name them all.
 *
 * All three values are read once per block and shape the filter coefficients, [db] included,
 * so an LFO on [db] zippers just like an LFO on a cutoff. For a smooth gain ride use a VCA
 * instead. See [tap] for the parallel alternative.
 */
@KlangScript.Function(name = "band")
internal fun EqBuilder.scriptBand(freq: IgnitorDslLike, q: IgnitorDslLike = 0.707, db: IgnitorDslLike = 0.0): EqBuilder =
    this.band(freq = freq.toIgnitorDsl(), q = q.toIgnitorDsl(), db = db.toIgnitorDsl())

/**
 * Adds a parallel resonant boost: takes the sound going INTO the equalizer, keeps only the band
 * around [freq], scales it by [gain] and mixes it back in. The short way to write
 * `signal.add(signal.bandpass(freq, q).mul(gain))`, in one pass.
 *
 * [gain] is a plain multiplier, not decibels: 1.0 mixes the band back in at full strength, 0 is
 * silent. [q] is the ordinary bandpass width, the same number `.bandpass()` takes. Taps mix with
 * the original sound rather than stacking on each other, so several taps stay predictable where
 * several [band] calls would compound. The engine bandpass is UNITY-peak at [freq], so [q] is a
 * pure WIDTH control: the boost at [freq] is `1 + gain` for ANY q. The default `tap(freq)` is
 * NOT silent: `1 + 1 = 2`, a lift of about 6 dB, where the default `band(freq)` is transparent.
 *
 * Give [gain] a number or a slot, not a moving signal: on a voice eq an Ignitor slot (`Ignitor.param`,
 * `Ignitor.slot.*`), on a chain eq a Katalyst param (`Katalyst.param`, `Katalyst.slot.*`). It is re-read only once
 * per block, so a swept tap gain steps instead of gliding. For that, use the chained
 * `signal.add(signal.bandpass(...).mul(lfo))` form, which is smooth.
 */
@KlangScript.Function(name = "tap")
internal fun EqBuilder.scriptTap(freq: IgnitorDslLike, q: IgnitorDslLike = 0.707, gain: IgnitorDslLike = 1.0): EqBuilder =
    this.tap(freq = freq.toIgnitorDsl(), q = q.toIgnitorDsl(), gain = gain.toIgnitorDsl())

/**
 * Adds a peaking band to the chain's equalizer: the voice eq's `band`, the same section and the same defaults
 * ([db] decibels at [freq], [q] the width; `db = 0` is transparent, a cut mirrors the same boost, the second positional
 * argument is [q], not gain). Each value is a number or a Katalyst param (`Katalyst.param`, `Katalyst.slot.*`), which
 * listens to `katp`; a Katalyst expression (`Kat.sine(0.1).range(200, 400)`) is a script error: a section reads a
 * number or a slot, once per block, never a moving knob.
 */
@KlangScript.Function(name = "band")
internal fun KatalystEqBuilder.scriptBand(freq: IgnitorDslLike, q: IgnitorDslLike = 0.707, db: IgnitorDslLike = 0.0): KatalystEqBuilder =
    this.band(freq = freq.toEqSectionKatalystKnob(), q = q.toEqSectionKatalystKnob(), db = db.toEqSectionKatalystKnob())

/**
 * Adds a parallel resonant boost to the chain's equalizer: the voice eq's `tap`, the same section and the same
 * defaults (the band around [freq] of the sound going INTO the equalizer, [q] its width, scaled by [gain], a plain
 * multiplier, and mixed back in; the default `tap(freq)` lifts about 6 dB). Each value is a number or a Katalyst param,
 * which listens to `katp`; a Katalyst expression is a script error, as on `band`.
 */
@KlangScript.Function(name = "tap")
internal fun KatalystEqBuilder.scriptTap(freq: IgnitorDslLike, q: IgnitorDslLike = 0.707, gain: IgnitorDslLike = 1.0): KatalystEqBuilder =
    this.tap(freq = freq.toEqSectionKatalystKnob(), q = q.toEqSectionKatalystKnob(), gain = gain.toEqSectionKatalystKnob())

// ── Phaser ───────────────────────────────────────────────────────────────────

/*
 * `PhaserBuilder`, `ShimmerBuilder` and `TremoloBuilder` live in `audio_bridge` (`EffectBuilders.kt` there, since the
 * typed-doors rollout S3c) with their typed member knobs; the script knobs below convert and call them.
 */

/**
 * Minimum dry coefficient of the phaser (default 0): the dry signal never drops below this share.
 * A knob on effect builders only, where it cannot meet the round-down `floor()` of a signal.
 */
@KlangScript.Function(name = "floor")
internal fun PhaserBuilder.scriptFloor(floor: IgnitorDslLike): PhaserBuilder = this.floor(floor = floor.toIgnitorDsl())

// ── Tremolo ──────────────────────────────────────────────────────────────────

/**
 * Where the tremolo's swing sits, in the -1..1 language of the Ignitor `range`: the LFO's low point maps to [from],
 * its high point to [to], and the gain is `1 + depth * that`, so the depth on the door stays the musical knob.
 * The default `range(-1, 0)` is the tremolo as it always was, the level dipping from 1 to `1 - depth`. `range(0, 1)`
 * swells upward from 1 to `1 + depth` (louder, so mind the headroom), `range(-1, 1)` swings both ways, `range(0, 2)`
 * twice the depth upward. Raw, no clamp. Both values are signals, read like the depth.
 *
 * ```KlangScript
 * Ignitor.saw().tremolo(4, 0.3, x => x.range(-1, 1))   // the level swings from 0.7 to 1.3
 * ```
 */
@KlangScript.Function(name = "range")
internal fun TremoloBuilder.scriptRange(from: IgnitorDslLike, to: IgnitorDslLike): TremoloBuilder =
    this.range(from = from.toIgnitorDsl(), to = to.toIgnitorDsl())

/**
 * The LFO's waveform: `"sine"` (default), `"triangle"`, `"square"`, `"sawtooth"` or `"ramp"`, with the
 * oscillator aliases (`"tri"`, `"sqr"`, `"pulse"`, `"saw"`, `"sin"`); an unknown name is sine. [name]
 * may also be the index in that list as a number, or a slot carrying it, and the door is the only
 * way to write one. Chosen once per note, so give it a name, a number or a slot: a moving signal
 * has no value to choose by and reads as sine. The shape IS the oscillator of that name; the square,
 * sawtooth and ramp get a 16 ms soft edge, so they pulse without clicking. A `square` at full depth
 * is silence for about half of every cycle, which is the point.
 */
@KlangScript.Function(name = "shape")
internal fun TremoloBuilder.scriptShape(name: IgnitorDslLike): TremoloBuilder =
    this.shape(index = catalogueIndex(name, node.shape, LfoShapes::indexOf))

// ── Vibrato ──────────────────────────────────────────────────────────────────

/*
 * `VibratoBuilder` lives in `audio_bridge` (`IgnitorBuilders.kt` there) with its typed member knobs; the script knobs
 * below are `internal` extensions with a Kotlin name of their own (`scriptRange`, the script word in `name`) that
 * convert and call those members, so a renamed or retyped member is a compile error, never a self-call
 * (`/dsl-design` section 3, the typed-door pattern).
 */

/**
 * Where the vibrato's swing sits, in the -1..1 language of the Ignitor `range`: the LFO's -1 maps to [from], its +1
 * to [to], and the depth on the door (in semitones) scales that, so it stays the musical knob. The default
 * `range(-1, 1)` swings both ways around the note, the vibrato as it always was. `range(0, 1)` swings only upward, as a
 * guitarist bends a fretted string; `range(-1, 0)` only downward; `range(1, -1)` turns the LFO upside down. Raw, no
 * clamp. Both values are signals, read like the depth. The LFO starts in the middle of the swing (`phase` 0), so an
 * upward-only vibrato starts on the note with `phase(0.75)`, a downward-only one with `phase(0.25)`.
 *
 * ```KlangScript
 * Ignitor.saw().vibrato(5, 0.5, v => v.range(0, 1).phase(0.75))   // from the note up to half a semitone, never below
 * ```
 */
@KlangScript.Function(name = "range")
internal fun VibratoBuilder.scriptRange(from: IgnitorDslLike, to: IgnitorDslLike): VibratoBuilder =
    this.range(from = from.toIgnitorDsl(), to = to.toIgnitorDsl())

/**
 * Where in its cycle the vibrato's LFO runs: a fraction of one cycle added to its phase every sample (default 0), the
 * oscillators' `phase` knob. Phase 0 starts the wobble in the middle of its swing, rising (on the note for the default
 * `range(-1, 1)`; a quarter of the depth sharp for `range(0, 1)`); 0.25 starts it at its top, 0.5 in the middle,
 * falling, 0.75 at its bottom (the note for `range(0, 1)`). It wraps: 1 is 0, 1.25 is 0.25, -0.25 is 0.75. A number is
 * the start phase; a signal moves the phase while the note plays (a jump is a jump in pitch).
 *
 * ```KlangScript
 * Ignitor.saw().vibrato(5, 0.5, v => v.phase(0.25))   // every note starts at the top of its wobble
 * ```
 */
@KlangScript.Function(name = "phase")
internal fun VibratoBuilder.scriptPhase(phase: IgnitorDslLike): VibratoBuilder = this.phase(phase = phase.toIgnitorDsl())

// ── Shimmer ──────────────────────────────────────────────────────────────────

/** Minimum dry coefficient of the shimmer (default 0); see `PhaserBuilder.floor`. */
@KlangScript.Function(name = "floor")
internal fun ShimmerBuilder.scriptFloor(floor: IgnitorDslLike): ShimmerBuilder = this.floor(floor = floor.toIgnitorDsl())

/**
 * The runtime hands numeric literals through as `Double`, so a `Boolean` parameter would throw a
 * raw ClassCastException on `humanize(1)`, and numeric flags are the established idiom on the sprudel
 * side (`adsrOn(0)`). Coerce rather than throw, per the project's user-facing-param rule.
 */
internal fun coerceFlag(flag: Any): Boolean = when (flag) {
    is Boolean -> flag
    is Number -> flag.toDouble() != 0.0
    else -> true
}
